import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../lib/language';
import { BaseStatementSearchParamType, StatementPeriodType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getStatements as getStatementsCall, getStatementsCashFlow } from '../../lib/store/fundamental';

import {
  BaseTable,
  FinancialPeriodButtons,
  FinancialsTableRow,
  GoBackButton,
  Preloader,
} from '../../components';
import SimpleFinancialTableRow from '../../components/SimpleFinancialTableRow/SimpleFinancialTableRow';
import { useIsMobile } from '../../hooks';
import TimeOptionsModal from '../SymbolDetails/Tabs/Ratios/Modals/TimeOptionsModal';

import styles from './CashFlowStatement.module.scss';

const CashFlowStatement = (props: ReduxProps) => {
  const { getAllStatements, getStatements, statementsData } = props;
  const { isMobile } = useIsMobile();

  const url = window.location.href;
  const symbol = url.split('/')[4];

  const [period, setPeriod] = useState <StatementPeriodType>(StatementPeriodType.ANNUAL);
  const [selectedPeriod, setSelectedPeriod] = useState <number>(1);
  const [headers, setHeaders] = useState <string[]>([]);
  const [data, setData] = useState <Array<Array<number | string>>>([]);
  const [loading, setLoading] = useState(true);
  const [currentTimeOption, setCurrentTimeOption] = useState(0);
  const [timeOptions, setTimeOptions] = useState<string[]>([]);
  const currentPeriod = currentTimeOption + 1;

  const tabs = [
    { text: lang.financialsAnnualOption(),
      component: <></>,
    },
    { text: lang.financialsQuarterlyOption(),
      component: <></>,
    },
  ];

  useEffect(() => {
    getAllStatements({
      symbol,
      periodType: period,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getStatements({
      symbol,
      periodType: period,
    });
  }, [getStatements, symbol, period]);

  useEffect(() => {
    if (statementsData.cashFlow.length > 0) {
      const headersArray: string[] = [];
      const operatingCashFlowArray: Array<number | string> = [];
      const netIncomeArray: Array<number | string> = [];
      const depreciationArray: Array<number | string> = [];
      const deferredIncomeTaxArray: Array<number | string> = [];
      const stockBasedCompensationArray: Array<number | string> = [];
      const changeInWorkingCapitalArray: Array<number | string> = [];
      const accountsReceivableArray: Array<number | string> = [];
      const changeInInventoryArray: Array<number | string> = [];
      const changeInAccountPayablesArray: Array<number | string> = [];
      const otherWorkingCapitalArray: Array<number | string> = [];
      const otherNonCashItemsArray: Array<number | string> = [];

      const investmentsCashFlowArray: Array<number | string> = [];
      const ppeInvestmentsArray: Array<number | string> = [];
      const acquisitionsArray: Array<number | string> = [];
      const saleOfInvestmentsArray: Array<number | string> = [];
      const purchaseOfInvestmentsArray: Array<number | string> = [];
      const otherInvestingActivitiesArray: Array<number | string> = [];

      const financingCashFlowChartArray: Array<number | string> = [];
      const deptRepaymentArray: Array<number | string> = [];
      const commonStockIssuedArray: Array<number | string> = [];
      const dividentsPaidArray: Array<number | string> = [];
      const otherFinancingActivitiesArray: Array<number | string> = [];

      const endCashPositionArray: Array<number | string> = [];
      const changeInCashArray: Array<number | string> = [];
      const beginningCashPositionArray: Array<number | string> = [];

      const capitalExpenditureArray: Array<number | string> = [];
      const freeCashFlowArray: Array<number | string> = [];

      statementsData.cashFlow.forEach((item) => {
        // eslint-disable-next-line no-unused-expressions
        headersArray.push(period === StatementPeriodType.ANNUAL ? item.calendarYear.toString() : `${item.period}'${item.calendarYear.toString().slice(-2)}`);
        operatingCashFlowArray.push(item.operatingCashFlow);
        netIncomeArray.push(item.netIncome);
        depreciationArray.push(item.depreciationAndAmortization);
        deferredIncomeTaxArray.push(item.deferredIncomeTax);
        stockBasedCompensationArray.push(item.stockBasedCompensation);
        changeInWorkingCapitalArray.push(item.changeInWorkingCapital);
        accountsReceivableArray.push(item.accountsReceivables);
        changeInInventoryArray.push(item.inventory);
        changeInAccountPayablesArray.push(item.accountsPayables);
        otherWorkingCapitalArray.push(item.otherWorkingCapital);
        otherNonCashItemsArray.push(item.otherNonCashItems);

        investmentsCashFlowArray.push(item.netCashUsedForInvestingActivities);
        ppeInvestmentsArray.push(item.investmentsInPropertyPlantAndEquipment);
        acquisitionsArray.push(item.acquisitionsNet);
        saleOfInvestmentsArray.push(item.salesMaturitiesOfInvestments);
        purchaseOfInvestmentsArray.push(item.purchasesOfInvestments);
        otherInvestingActivitiesArray.push(item.otherInvestingActivities);

        financingCashFlowChartArray.push(item.netCashUsedProvidedByFinancingActivities);
        deptRepaymentArray.push(item.debtRepayment);
        commonStockIssuedArray.push(item.commonStockIssued);
        dividentsPaidArray.push(item.dividendsPaid);
        otherFinancingActivitiesArray.push(item.otherFinancingActivities);

        endCashPositionArray.push(item.cashAtEndOfPeriod);
        changeInCashArray.push(item.netChangeInCash);
        beginningCashPositionArray.push(item.cashAtBeginningOfPeriod);

        capitalExpenditureArray.push(item.capitalExpenditure);
        freeCashFlowArray.push(item.freeCashFlow);
      });

      !isMobile && headersArray.push(lang.typeHeader());
      operatingCashFlowArray.push(lang.financialsOperatingCashFlow());
      netIncomeArray.push(lang.financialsNetIncome());
      depreciationArray.push(lang.financialsDeprecationAndAmortization());
      deferredIncomeTaxArray.push(lang.financialsDeferredIncomeTax());
      stockBasedCompensationArray.push(lang.financialsStockBasedCompensation());
      changeInWorkingCapitalArray.push(lang.financialsChangeInWorkingCapital());
      accountsReceivableArray.push(lang.financialsChangeInAccountsReceivable());
      changeInInventoryArray.push(lang.financialsChangeInInventory());
      changeInAccountPayablesArray.push(lang.financialsChangeInAccountsPayables());
      otherWorkingCapitalArray.push(lang.financialsOtherWorkingCapital());
      otherNonCashItemsArray.push(lang.financialsOtherNonCashItems());

      investmentsCashFlowArray.push(lang.financialsInvestingCashFlow());
      ppeInvestmentsArray.push(lang.financialsPPEInvestments());
      acquisitionsArray.push(lang.financialsAcquisitionsNet());
      saleOfInvestmentsArray.push(lang.financialsSaleOfInvestments());
      purchaseOfInvestmentsArray.push(lang.financialsPurchaseOfInvestments());
      otherInvestingActivitiesArray.push(lang.financialsInvestingOther());

      financingCashFlowChartArray.push(lang.financialsFinancingCashFlow());
      deptRepaymentArray.push(lang.financialsDebtRepayment());
      commonStockIssuedArray.push(lang.financialsCommonStockRepurchased());
      dividentsPaidArray.push(lang.financialsDividendsPaid());
      otherFinancingActivitiesArray.push(lang.financialsFinancingOther());

      endCashPositionArray.push(lang.financialsEndCashPosition());
      changeInCashArray.push(lang.financialsChangesInCash());
      beginningCashPositionArray.push(lang.financialsBeginningCashPosition());

      capitalExpenditureArray.push(lang.financialsCapitalExpenditure());
      freeCashFlowArray.push(lang.financialsFreeCashFlow());

      setHeaders(headersArray.reverse());
      setData([
        operatingCashFlowArray.reverse(),
        netIncomeArray.reverse(),
        depreciationArray.reverse(),
        deferredIncomeTaxArray.reverse(),
        stockBasedCompensationArray.reverse(),
        changeInWorkingCapitalArray.reverse(),
        accountsReceivableArray.reverse(),
        changeInInventoryArray.reverse(),
        changeInAccountPayablesArray.reverse(),
        otherWorkingCapitalArray.reverse(),
        otherNonCashItemsArray.reverse(),
        investmentsCashFlowArray.reverse(),
        ppeInvestmentsArray.reverse(),
        acquisitionsArray.reverse(),
        saleOfInvestmentsArray.reverse(),
        purchaseOfInvestmentsArray.reverse(),
        otherInvestingActivitiesArray.reverse(),
        financingCashFlowChartArray.reverse(),
        deptRepaymentArray.reverse(),
        commonStockIssuedArray.reverse(),
        dividentsPaidArray.reverse(),
        otherFinancingActivitiesArray.reverse(),
        endCashPositionArray.reverse(),
        changeInCashArray.reverse(),
        beginningCashPositionArray.reverse(),
        capitalExpenditureArray.reverse(),
        freeCashFlowArray.reverse(),
      ]);
    }
  // Period shouldn't be a dependency here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementsData, isMobile]);

  useEffect(() => {
    const options = headers.filter((header) => header !== lang.typeHeader() || !isMobile);
    setTimeOptions(options);
  }, [headers, isMobile]);

  useEffect(() => {
    setCurrentTimeOption(0);
  }, [period]);

  useEffect(() => {
    if (data.length > 0) {
      setLoading(false);
    }
  }, [data, setLoading]);

  const handlePeriodChange = (index: number) => {
    setPeriod(index === 0 ? StatementPeriodType.ANNUAL : StatementPeriodType.QUARTERLY);
  };

  if (loading) {
    return (
      <div className={styles.loader}>
        <Preloader isFullScreen text="" owner="cashflow-statement" />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>

      {!isMobile && (
        <div className={styles.backButton}>
          <GoBackButton />
        </div>
      )}

      <div className={styles.header}>
        <div className={styles.title}>
          {lang.financialsCashFlow()}
        </div>
      </div>
      <div className={styles.periodButtonsWrapper}>
        <FinancialPeriodButtons currentPeriod={period} setPeriod={setPeriod} />
      </div>
      <div className={classNames(styles.tableWrapper, { [styles.tableWrapperMob]: isMobile })}>
        {isMobile ? (
          <>
            <div className={styles.periodButtons}>
              <TimeOptionsModal
                options={timeOptions}
                currentOption={timeOptions[currentTimeOption]}
                onTimeChange={(index) => setCurrentTimeOption(index)}
                isDisabled={false}
                currentPeriod={period}
              />
            </div>
            <SimpleFinancialTableRow
              type="Type"
              value={timeOptions[currentTimeOption] || 'N/A'}
            />
            <div className={styles.wrapperInfo}>
              <FinancialsTableRow data={[data[0]]} isClickable currentPeriod={currentPeriod}>
                <FinancialsTableRow data={[data[1]]} currentPeriod={currentPeriod} isBaseFontRow />
                <FinancialsTableRow data={[data[2]]} currentPeriod={currentPeriod} isBaseFontRow />
                <FinancialsTableRow data={[data[3]]} currentPeriod={currentPeriod} isBaseFontRow />
                <FinancialsTableRow data={[data[4]]} currentPeriod={currentPeriod} isBaseFontRow />
                <FinancialsTableRow
                  data={data.slice(5, 10)}
                  isClickable
                  currentPeriod={selectedPeriod}
                  isMaxNestLevel
                />
                <FinancialsTableRow data={[data[10]]} currentPeriod={currentPeriod} isBaseFontRow />
              </FinancialsTableRow>
              <FinancialsTableRow data={data.slice(11, 17)} isClickable currentPeriod={selectedPeriod} />
              <FinancialsTableRow data={data.slice(17, 22)} isClickable currentPeriod={selectedPeriod} />
              <FinancialsTableRow data={data.slice(22, 25)} isClickable currentPeriod={selectedPeriod} />
              <FinancialsTableRow data={data.slice(25)} currentPeriod={selectedPeriod} />
              <FinancialsTableRow data={data.slice(26)} currentPeriod={selectedPeriod} />
            </div>
          </>
        ) : (
          <BaseTable>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
            <FinancialsTableRow data={[data[0]]} isClickable isCashflow>
              <FinancialsTableRow data={[data[1]]} />
              <FinancialsTableRow data={[data[2]]} />
              <FinancialsTableRow data={[data[3]]} />
              <FinancialsTableRow data={[data[4]]} />
              <FinancialsTableRow
                data={data.slice(5, 10)}
                isClickable
                currentPeriod={selectedPeriod}
                isMaxNestLevel
              />
              <FinancialsTableRow data={[data[10]]} />
            </FinancialsTableRow>
            <FinancialsTableRow data={data.slice(11, 17)} isClickable bigLine />
            <FinancialsTableRow data={data.slice(17, 22)} isClickable bigLine />
            <FinancialsTableRow data={data.slice(22, 25)} isClickable bigLine />
            <FinancialsTableRow data={[data[25]]} bigLine />
            <FinancialsTableRow data={[data[26]]} />
          </BaseTable>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  statementsData: state.fundamental.statementsData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getAllStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsCall(params)),
  getStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsCashFlow(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
    type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CashFlowStatement);
